'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { StructuredDataFaq } from '@/services/structured-data-pages.service';

import FaqSchema from '../structured-data/FaqSchema';

import Faq from './Faq';

type StructuredDataPage = {
  id: number;
  path: string;
  faq: StructuredDataFaq[];
};

type FaqResponse = {
  data: StructuredDataPage[];
};

type FaqCache = {
  data: StructuredDataPage[];
  timestamp: number;
};

const FAQ_CACHE_KEY = 'kia-structured-data-faq';
const FAQ_CACHE_TTL = 60 * 60 * 1000; // 1 hour

let faqRequest: Promise<StructuredDataPage[]> | null = null;

const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < FAQ_CACHE_TTL;
};

const fetchFaqPages = async (): Promise<StructuredDataPage[]> => {
  const cachedFaq = sessionStorage.getItem(FAQ_CACHE_KEY);

  if (cachedFaq) {
    try {
      const parsedCache: FaqCache = JSON.parse(cachedFaq);

      if (
        Array.isArray(parsedCache.data) &&
        isCacheValid(parsedCache.timestamp)
      ) {
        return parsedCache.data;
      }

      sessionStorage.removeItem(FAQ_CACHE_KEY);
    } catch {
      sessionStorage.removeItem(FAQ_CACHE_KEY);
    }
  }

  if (faqRequest) {
    return faqRequest;
  }

  faqRequest = fetch('/api/faq')
    .then(async (response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch FAQ data');
      }

      const result: FaqResponse = await response.json();
      const data = result.data ?? [];

      const cache: FaqCache = {
        data,
        timestamp: Date.now(),
      };

      sessionStorage.setItem(
        FAQ_CACHE_KEY,
        JSON.stringify(cache),
      );

      return data;
    })
    .finally(() => {
      faqRequest = null;
    });

  return faqRequest;
};

const FaqWrapper = () => {
  const pathname = usePathname();

  const [faqs, setFaqs] = useState<StructuredDataFaq[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!pathname) {
      return;
    }

    let cancelled = false;

    const loadFaq = async () => {
      try {
        const pages = await fetchFaqPages();

        if (cancelled) {
          return;
        }

        const page = pages.find(
          (item) => item.path === pathname,
        );

        setFaqs(page?.faq ?? []);
        setLoaded(true);
      } catch {
        if (!cancelled) {
          setFaqs([]);
          setLoaded(true);
        }
      }
    };

    loadFaq();

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  if (!pathname || !loaded || !faqs.length) {
    return null;
  }

  return (
    <>
      <FaqSchema faqs={faqs} />
      <Faq faqs={faqs} />
    </>
  );
};

export default FaqWrapper;